/**
 * Client-side file type registry — mirrors backend/utils/fileTypes.js (a backend test fails if they drift).
 * Used by UploadDropzone and AssetLibraryPanel for UI display and validation.
 *
 * capability (what DoAR can do with the stored source TODAY):
 *   DIRECT           usable as-is; a real renderer/loader runs for it (or it is a final delivery format).
 *   REAL_CONVERSION  a conversion that genuinely executes exists (video -> MP4 via ffmpeg).
 *   SOURCE_ONLY      stored durably, but there is no executable preview or conversion yet.
 *   REJECTED         the backend does not accept this upload at all.
 * browserReady === "DoAR can show it in a browser now" (never true for SOURCE_ONLY).
 */

export const FILE_REGISTRY = [
  // Images
  { ext: ".jpg", category: "image", label: "JPEG Image", icon: "🖼", capability: "DIRECT", browserReady: true },
  { ext: ".jpeg", category: "image", label: "JPEG Image", icon: "🖼", capability: "DIRECT", browserReady: true },
  { ext: ".png", category: "image", label: "PNG Image", icon: "🖼", capability: "DIRECT", browserReady: true },
  { ext: ".webp", category: "image", label: "WebP Image", icon: "🖼", capability: "DIRECT", browserReady: true },
  { ext: ".gif", category: "image", label: "GIF Image", icon: "🎞", capability: "DIRECT", browserReady: true },
  { ext: ".avif", category: "image", label: "AVIF Image", icon: "🖼", capability: "REJECTED", browserReady: false },
  { ext: ".bmp", category: "image", label: "Bitmap Image", icon: "🖼", capability: "DIRECT", browserReady: true },
  { ext: ".svg", category: "image", label: "SVG Image", icon: "✏", capability: "DIRECT", browserReady: true },
  { ext: ".tiff", category: "image", label: "TIFF Image", icon: "🖼", capability: "SOURCE_ONLY", browserReady: false },
  { ext: ".tif", category: "image", label: "TIFF Image", icon: "🖼", capability: "SOURCE_ONLY", browserReady: false },

  // Video
  { ext: ".mp4", category: "video", label: "MP4 Video", icon: "▶", capability: "DIRECT", browserReady: true },
  { ext: ".webm", category: "video", label: "WebM Video", icon: "▶", capability: "DIRECT", browserReady: true },
  { ext: ".ogv", category: "video", label: "OGG Video", icon: "▶", capability: "REJECTED", browserReady: false },
  { ext: ".mov", category: "video", label: "QuickTime Video", icon: "▶", capability: "DIRECT", browserReady: true },
  { ext: ".m4v", category: "video", label: "M4V Video", icon: "▶", capability: "REJECTED", browserReady: false },
  { ext: ".avi", category: "video", label: "AVI Video", icon: "▶", capability: "REAL_CONVERSION", browserReady: false },
  { ext: ".mkv", category: "video", label: "MKV Video", icon: "▶", capability: "REAL_CONVERSION", browserReady: false },

  // Audio
  { ext: ".mp3", category: "audio", label: "MP3 Audio", icon: "♫", capability: "DIRECT", browserReady: true },
  { ext: ".wav", category: "audio", label: "WAV Audio", icon: "♫", capability: "DIRECT", browserReady: true },
  { ext: ".ogg", category: "audio", label: "OGG Audio", icon: "♫", capability: "DIRECT", browserReady: true },
  { ext: ".m4a", category: "audio", label: "M4A Audio", icon: "♫", capability: "DIRECT", browserReady: true },
  { ext: ".aac", category: "audio", label: "AAC Audio", icon: "♫", capability: "DIRECT", browserReady: true },
  { ext: ".flac", category: "audio", label: "FLAC Audio", icon: "♫", capability: "DIRECT", browserReady: true },

  // 3D — browser-ready
  { ext: ".glb", category: "model3d", label: "GLB Model", icon: "◈", capability: "DIRECT", browserReady: true },
  { ext: ".gltf", category: "model3d", label: "GLTF Model", icon: "◈", capability: "DIRECT", browserReady: true },
  { ext: ".obj", category: "model3d", label: "OBJ Model", icon: "◈", capability: "DIRECT", browserReady: true },
  { ext: ".stl", category: "model3d", label: "STL Model", icon: "◈", capability: "DIRECT", browserReady: true },
  { ext: ".usdz", category: "model3d", label: "USDZ Model", icon: "◈", capability: "DIRECT", browserReady: false },
  { ext: ".dae", category: "model3d", label: "Collada Model", icon: "◈", capability: "DIRECT", browserReady: true },

  // 3D — needs conversion
  { ext: ".fbx", category: "model3d", label: "FBX Model", icon: "◈", capability: "DIRECT", browserReady: true },
  { ext: ".blend", category: "model3d", label: "Blender Scene", icon: "◈", capability: "SOURCE_ONLY", browserReady: false },

  // CAD / BIM
  { ext: ".skp", category: "cad_bim", label: "SketchUp Model", icon: "🏗", capability: "SOURCE_ONLY", browserReady: false },
  { ext: ".rvt", category: "cad_bim", label: "Revit Model", icon: "🏗", capability: "SOURCE_ONLY", browserReady: false },
  { ext: ".ifc", category: "cad_bim", label: "IFC Model", icon: "🏗", capability: "SOURCE_ONLY", browserReady: false },
  { ext: ".dwg", category: "cad_bim", label: "AutoCAD Drawing", icon: "📐", capability: "SOURCE_ONLY", browserReady: false },
  { ext: ".dxf", category: "cad_bim", label: "DXF Drawing", icon: "📐", capability: "SOURCE_ONLY", browserReady: false },
  { ext: ".3dm", category: "cad_bim", label: "Rhino Model", icon: "🏗", capability: "SOURCE_ONLY", browserReady: false },

  // Documents
  { ext: ".pdf", category: "document", label: "PDF Document", icon: "📄", capability: "DIRECT", browserReady: true },
  { ext: ".txt", category: "document", label: "Text File", icon: "📝", capability: "DIRECT", browserReady: true },
  { ext: ".doc", category: "document", label: "Word Document", icon: "📄", capability: "SOURCE_ONLY", browserReady: false },
  { ext: ".docx", category: "document", label: "Word Document", icon: "📄", capability: "SOURCE_ONLY", browserReady: false },
  { ext: ".rtf", category: "document", label: "RTF Document", icon: "📄", capability: "REJECTED", browserReady: false },
  { ext: ".ppt", category: "document", label: "PowerPoint", icon: "📊", capability: "SOURCE_ONLY", browserReady: false },
  { ext: ".pptx", category: "document", label: "PowerPoint", icon: "📊", capability: "SOURCE_ONLY", browserReady: false },

  // Point cloud
  { ext: ".las", category: "pointcloud", label: "LAS Point Cloud", icon: "✦", capability: "SOURCE_ONLY", browserReady: false },
  { ext: ".laz", category: "pointcloud", label: "LAZ Point Cloud", icon: "✦", capability: "SOURCE_ONLY", browserReady: false },
  { ext: ".ply", category: "model3d", label: "PLY Point Cloud", icon: "✦", capability: "DIRECT", browserReady: true },
  { ext: ".e57", category: "pointcloud", label: "E57 Scan", icon: "✦", capability: "SOURCE_ONLY", browserReady: false },
  { ext: ".pts", category: "pointcloud", label: "PTS Point Cloud", icon: "✦", capability: "SOURCE_ONLY", browserReady: false },
  { ext: ".xyz", category: "pointcloud", label: "XYZ Point Cloud", icon: "✦", capability: "SOURCE_ONLY", browserReady: false },
];

const _BY_EXT = Object.fromEntries(FILE_REGISTRY.map((e) => [e.ext, e]));

export function getFileInfo(ext) {
  return _BY_EXT[(ext || "").toLowerCase()] || null;
}

export function getFileInfoFromName(name) {
  const ext = (name || "").lastIndexOf(".") !== -1
    ? name.slice(name.lastIndexOf(".")).toLowerCase()
    : "";
  return getFileInfo(ext);
}

export function isBrowserReady(ext) {
  return getFileInfo(ext)?.browserReady ?? false;
}

export function getCategory(ext) {
  return getFileInfo(ext)?.category ?? "unknown";
}

/** Accepted as an upload (source content DoAR will store). Says nothing about preview or conversion. */
export function isSupported(ext) {
  const info = getFileInfo(ext);
  return !!info && info.capability !== "REJECTED";
}

export function getCapability(ext) {
  return getFileInfo(ext)?.capability ?? "REJECTED";
}

export function isSupportedFile(file) {
  const name = file?.name || "";
  const ext  = name.lastIndexOf(".") !== -1 ? name.slice(name.lastIndexOf(".")).toLowerCase() : "";
  return isSupported(ext);
}

export const CATEGORY_LABELS = {
  image:      "Image",
  video:      "Video",
  audio:      "Audio",
  model3d:    "3D Model",
  cad_bim:    "CAD / BIM",
  document:   "Document",
  pointcloud: "Point Cloud",
  unknown:    "Unknown",
};

export const CATEGORY_COLORS = {
  image:      "#34d399",
  video:      "#fb923c",
  audio:      "#f59e0b",
  model3d:    "#00c8ff",
  cad_bim:    "#a78bfa",
  document:   "#fbbf24",
  pointcloud: "#f472b6",
  unknown:    "#6b7280",
};

// All accepted extensions for the HTML file input accept attribute
export const ACCEPT_ALL = FILE_REGISTRY.filter((e) => e.capability !== "REJECTED").map((e) => e.ext).join(",");

export function formatBytes(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
